import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Ship, FileText, Receipt, DollarSign, Users, LogOut, LayoutDashboard, Anchor, FileSignature, Shield, Settings, BarChart3, Menu, X, Calculator, ClipboardList } from 'lucide-react';
import api from '../utils/api';
import LanguageSwitcher from './LanguageSwitcher';

// Map paths to permission keys
const PATH_TO_PERMISSION = {
  '/': 'dashboard',
  '/clients': 'clients',
  '/vessels': 'vessels',
  '/proformas': 'proformas',
  '/port-calls': 'port_calls',
  '/anchorage': 'anchorage',
  '/motor-launches': 'motor_launches',
  '/invoices': 'invoices',
  '/transaction-tracking': 'invoices', // Uses invoices permission
  '/payments': 'payments',
  '/documents': 'documents',
  '/tariff-calculator': 'proformas', // Use proformas permission
  '/raporlama': 'reports',
  '/admin': 'admin_panel',
  '/settings': 'settings'
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarLogo, setSidebarLogo] = useState(null);

  useEffect(() => {
    // Fetch sidebar logo from settings
    const fetchLogo = async () => {
      try {
        const response = await api.get('/settings');
        if (response.data.sidebar_logo) {
          setSidebarLogo(response.data.sidebar_logo);
        }
      } catch (error) {
        console.log('Using default logo');
      }
    };
    fetchLogo();
  }, []);

  const allNavigation = [
    { name: t('nav.dashboard'), path: '/', icon: LayoutDashboard, permissionKey: 'dashboard' },
    { name: t('nav.clients'), path: '/clients', icon: Users, permissionKey: 'clients' },
    { name: t('nav.vessels'), path: '/vessels', icon: Ship, permissionKey: 'vessels' },
    { name: t('nav.proformas'), path: '/proformas', icon: FileText, permissionKey: 'proformas' },
    { name: t('nav.portCalls'), path: '/port-calls', icon: Anchor, permissionKey: 'port_calls' },
    { name: t('nav.anchorage'), path: '/anchorage', icon: Anchor, permissionKey: 'anchorage' },
    { name: t('nav.motorLaunches'), path: '/motor-launches', icon: Ship, permissionKey: 'motor_launches' },
    { name: t('nav.invoices'), path: '/invoices', icon: Receipt, permissionKey: 'invoices' },
    { name: t('nav.transactionTracking'), path: '/transaction-tracking', icon: ClipboardList, permissionKey: 'invoices' },
    { name: t('nav.payments'), path: '/payments', icon: DollarSign, permissionKey: 'payments' },
    { name: t('nav.documents'), path: '/documents', icon: FileSignature, permissionKey: 'documents' },
    { name: t('nav.tariffCalculator'), path: '/tariff-calculator', icon: Calculator, permissionKey: 'proformas' },
    { name: t('nav.reports'), path: '/raporlama', icon: BarChart3, permissionKey: 'reports' },
    { name: t('nav.admin'), path: '/admin', icon: Shield, permissionKey: 'admin_panel' },
    { name: t('nav.settings'), path: '/settings', icon: Settings, permissionKey: 'settings' },
  ];

  // Filter navigation based on user permissions
  const navigation = allNavigation.filter(item => {
    // Check user's module permissions - admin role no longer bypasses permissions
    const permissions = user?.module_permissions || {};
    
    // If permission is explicitly set to false, hide the menu item
    if (permissions[item.permissionKey] === false) return false;
    
    // If permission is true or not set, show the menu item
    return permissions[item.permissionKey] !== false;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleName = (role) => {
    const roleNames = {
      admin: 'Yönetici',
      operations: 'Operasyon',
      accounting: 'Muhasebe',
      viewer: 'İzleyici'
    };
    return roleNames[role] || role;
  };

  return (
    <div className="app-layout">
      {/* Mobile Hamburger */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        aria-label="Toggle menu"
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            padding: '0.5rem'
          }}>
            <img 
              src={sidebarLogo || "/rota-logo-new.png"} 
              alt="Rota Maritime Agency" 
              style={{ 
                height: '55px', 
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
                mixBlendMode: sidebarLogo ? 'normal' : 'multiply'
              }} 
            />
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.name.toLowerCase()}`}
                onClick={() => setIsSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info" data-testid="user-info">
            <div className="user-avatar">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.name}</div>
              <div className="user-role">{getRoleName(user?.role)}</div>
            </div>
          </div>
          <div className="sidebar-actions">
            <LanguageSwitcher />
            <button 
              className="btn btn-secondary" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </aside>
      
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;